import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, setViewMode } from '../../store/slices/authSlice';
import { fetchWaUnreadTotal } from '../../store/slices/whatsappSlice';
import QRScanner from '../QRScanner';
import JobCardPreviewModal from '../JobCardPreviewModal';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import Header from './Header';
import MobileBottomNav from './MobileBottomNav';
import AnnouncementBar from '../AnnouncementBar';
import QuickCreateMenu from './QuickCreateMenu';
import CommandPalette from '../ui/CommandPalette';
import SignInBranchGate from '../SignInBranchGate';
import { getMenuItems, menuFeatureAllowed } from './menuConfig';
import { hasPermission } from '../../utils/permissions';
import useVisiblePolling from '../../hooks/useVisiblePolling';
import { BACKGROUND_READ_TIMEOUT_MS } from '../../utils/readTimeout';
import { APPROVALS_CHANGED } from '../../utils/approvalLimits';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  // Collapsed unless the user pinned it open. The rail keeps every destination
  // one click away, and the screen space belongs to the work. Expanding is the
  // deliberate choice, so it is the one that gets remembered — an absent key
  // means "never asked for it", not "expand".
  const [navExpanded, setNavExpanded] = useState(
    () => localStorage.getItem('mm-nav-expanded') === '1'
  );
  const [showScanner, setShowScanner] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [previewJobCard, setPreviewJobCard] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, viewMode } = useSelector((state) => state.auth);
  const unreadTotal = useSelector((state) => state.whatsapp?.unreadTotal ?? 0);
  const { t, i18n } = useTranslation();

  // Keep document direction in sync on live language switch
  // (first paint is handled by the inline script in public/index.html)
  useEffect(() => {
    // startsWith guards any cached region code (e.g. 'ar-SA') from older sessions.
    const lng = (i18n.language || 'en').slice(0, 2);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
  }, [i18n.language]);

  // WhatsApp sidebar unread badge: poll a lightweight authorized total whenever
  // the user has the chat permission and the feature is enabled. The permission
  // and feature gates below are the client half; the endpoint re-checks both.
  // P01: the 30s cycle runs only while the tab is visible and resyncs on
  // return/reconnect — the badge is still refreshed, just not into a hidden tab.
  const canChatWhatsapp = hasPermission(user, 'whatsapp', 'chat');
  const whatsappFeatureOn = user?.company?.features?.includes('whatsapp');
  useVisiblePolling(
    () => dispatch(fetchWaUnreadTotal()),
    30000,
    { enabled: Boolean(canChatWhatsapp && whatsappFeatureOn) }
  );

  // Close the drawer when navigating on mobile
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Global Ctrl/Cmd+K → command palette
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Auto-logout after 30 minutes of inactivity. Activity is shared across tabs
  // via localStorage so working in any tab keeps the session alive; an interval
  // checks the idle gap and signs out when it is exceeded.
  useEffect(() => {
    const IDLE_LIMIT_MS = 30 * 60 * 1000;
    const KEY = 'mm-last-activity';
    const mark = () => {
      try { localStorage.setItem(KEY, String(Date.now())); } catch (e) { /* ignore */ }
    };
    mark();
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last > 10000) { last = now; mark(); } // throttle writes to ~10s
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    const interval = setInterval(() => {
      let ts = 0;
      try { ts = parseInt(localStorage.getItem(KEY) || '0', 10); } catch (e) { ts = 0; }
      if (ts && Date.now() - ts > IDLE_LIMIT_MS) {
        clearInterval(interval);
        events.forEach((ev) => window.removeEventListener(ev, onActivity));
        try { localStorage.removeItem(KEY); } catch (e) { /* ignore */ }
        toast(t('session.expiredIdle', 'Signed out due to 30 minutes of inactivity'));
        dispatch(logout());
        navigate('/login');
      }
    }, 30000);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearInterval(interval);
    };
  }, [dispatch, navigate, t]);

  const toggleNavExpanded = () => {
    setNavExpanded((v) => {
      localStorage.setItem('mm-nav-expanded', v ? '0' : '1');
      return !v;
    });
  };

  const toggleLanguage = () => {
    // Asked the other way round on purpose: "is it Arabic?" rather than "is it
    // English?". Anything that is not Arabic — 'en', 'en-US', a device locale we
    // do not ship — goes to Arabic, so no value can ever make this press do
    // nothing. i18n.js normalizes the code as well; this is the belt.
    const newLang = (i18n.language || '').slice(0, 2) === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    toast.success(t(newLang === 'ar' ? 'cm.header.switchedToArabic' : 'cm.header.switchedToEnglish'));
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleViewModeSwitch = () => {
    const newMode = viewMode === 'admin' ? 'staff' : 'admin';
    dispatch(setViewMode(newMode));
    navigate('/');
    toast.success(t(newMode === 'admin' ? 'cm.header.switchToAdminView' : 'cm.header.switchToStaffView'));
  };

  const handleScan = async (qrData) => {
    try {
      setShowScanner(false);

      const isStaff = user?.role?.name === 'Staff' || (viewMode === 'staff' && user?.role?.name !== 'Super Admin');
      const jobPath = (id) => (isStaff ? `/jobs/${id}` : `/job-cards/${id}`);

      const raw = (qrData || '').trim();
      if (!raw) {
        toast.error(t('cm.qr.invalid', { defaultValue: 'Invalid QR code' }));
        return;
      }

      // URL-encoded QR (e.g. https://motormind.autos/job-cards/123 or .../qr/123):
      // the trailing segment is the job-card DB id — navigate straight to it.
      const urlMatch = raw.match(/\/(?:job-cards|qr)\/([^/?#\s]+)/);
      if (urlMatch) {
        navigate(jobPath(urlMatch[1]));
        return;
      }

      // Everything else — a bare job_number (e.g. "TXC-2506-0001") or the legacy
      // JSON payload {type,id,number,...} — is resolved by the backend, which
      // returns the canonical job card. Navigate by its numeric id (never by the
      // scanned string: SPA routes expect the DB id, not the job_number).
      const response = await axios.post('/api/job-cards/scan-qr', { qrData: raw });
      const jobCard = response.data?.data;
      if (!jobCard?.id) {
        toast.error(t('cm.qr.notFound', { defaultValue: 'Job card not found' }));
        return;
      }
      navigate(jobPath(jobCard.id));
    } catch (error) {
      console.error('Error scanning QR code:', error);
      // Ported from MyJobs' own (now-removed) scanner: 403 = the code resolved
      // to a job card in a DIFFERENT garage — a distinct message from a plain
      // 404 not-found so the mechanic knows the scan worked but the card isn't
      // theirs.
      const status = error.response?.status;
      toast.error(
        status === 404
          ? t('cm.qr.notFound', { defaultValue: 'Job card not found' })
          : status === 403
            ? t('qrScanForeignGarage', { defaultValue: 'This job belongs to a different garage' })
            : t('cm.qr.invalid', { defaultValue: 'Invalid QR code' })
      );
    }
  };

  const roleName = user?.role?.name;
  const isStaffExperience = roleName === 'Staff' || (viewMode === 'staff' && roleName !== 'Super Admin');
  // In staff view the router only mounts the staff routes, so the sidebar must
  // show the staff menu too — otherwise admin menu items dead-end at "My Jobs".
  const menuItems = getMenuItems(isStaffExperience ? 'Staff' : roleName).filter((item) => {
    // `permission` = single required grant (AND). `permissionAny` = list where any
    // one grant suffices (OR) — used by hubs that surface more than one engine.
    if (item.permission && !hasPermission(user, item.permission.resource, item.permission.action)) {
      return false;
    }
    if (
      Array.isArray(item.permissionAny) &&
      !item.permissionAny.some((p) => hasPermission(user, p.resource, p.action))
    ) {
      return false;
    }
    // Same predicate as the server's requireFeature — including the fallback
    // for a company whose feature selection was never recorded. A raw
    // `features.includes()` here would hide half the menu from every tenant
    // that predates per-key gating.
    if (!menuFeatureAllowed(user?.company, item)) {
      return false;
    }
    return true;
  });

  // Approvals sidebar badge: what waits on this user's decision. Same cycle
  // as the bell (30s, visible tab only) and a bounded read (B02), so a hung
  // count cannot hold the poller's slot; refreshed at once after a decision
  // on the Approvals page. The last known count stays on a failed read.
  // Polled only when the menu shows the item (its permissionAny is the key
  // gate): a role whose menu has no Approvals entry (Staff, Super Admin) would
  // be counting for a badge it never shows.
  const canApprove = menuItems.some((item) => item.path === '/approvals');
  const [approvalsCount, setApprovalsCount] = useState(0);
  const fetchApprovalsCount = async () => {
    try {
      const res = await axios.get('/api/approvals/count', { timeout: BACKGROUND_READ_TIMEOUT_MS });
      const count = Number(res.data?.data?.count);
      setApprovalsCount(Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0);
    } catch (err) {
      // Background miss: keep the last count, no toast (as the bell does).
    }
  };
  useVisiblePolling(fetchApprovalsCount, 30000, { enabled: canApprove });
  useEffect(() => {
    if (!canApprove) return undefined;
    window.addEventListener(APPROVALS_CHANGED, fetchApprovalsCount);
    return () => window.removeEventListener(APPROVALS_CHANGED, fetchApprovalsCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canApprove]);

  // Curated primary destinations for the phone bottom bar (non-staff). The full
  // menu is one tap away under "More" (opens the sidebar drawer). Falls back to
  // the first available items if a preferred path is permission-filtered out.
  const MOBILE_PRIMARY_PATHS = {
    'Super Admin': ['/', '/companies', '/analytics'],
    Receptionist: ['/', '/job-cards', '/customers'],
    default: ['/', '/job-cards', '/invoices'],
  };
  const preferredPaths = MOBILE_PRIMARY_PATHS[roleName] || MOBILE_PRIMARY_PATHS.default;
  const mobilePrimary = preferredPaths
    .map((p) => menuItems.find((i) => i.path === p))
    .filter(Boolean);
  // Top up to 3 from the remaining menu if any preferred path was unavailable.
  for (const item of menuItems) {
    if (mobilePrimary.length >= 3) break;
    if (!mobilePrimary.includes(item)) mobilePrimary.push(item);
  }

  const pageTitle = (() => {
    if (location.pathname.startsWith('/job-cards/')) return t('jobCards');
    if (location.pathname.startsWith('/settings')) return t('settings');
    const menuItem = menuItems.find((item) => item.path === location.pathname);
    return menuItem ? t(menuItem.i18nKey) : t('dashboard');
  })();

  return (
    <div className="flex h-app bg-canvas">
      <Sidebar
        user={user}
        menuItems={menuItems}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onLogout={handleLogout}
        expanded={navExpanded}
        onToggleExpand={toggleNavExpanded}
        whatsappUnreadCount={unreadTotal}
        approvalsCount={canApprove ? approvalsCount : 0}
      />

      <div
        className={`flex-1 min-w-0 flex flex-col overflow-hidden transition-[margin] duration-200 ease-in-out ${
          navExpanded ? 'xl:ms-64' : 'xl:ms-20'
        }`}
      >
        <Header
          user={user}
          viewMode={viewMode}
          isStaffExperience={isStaffExperience}
          title={pageTitle}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          onToggleLanguage={toggleLanguage}
          onViewModeSwitch={handleViewModeSwitch}
          onScan={() => setShowScanner(true)}
          onOpenSearch={() => setPaletteOpen(true)}
          onLogout={handleLogout}
        />

        {/* P8: after a login by a multi-branch user, pick the branch to work in
            before anything else happens. Reads its own sessionStorage flag. */}
        <SignInBranchGate />

        <main className="flex-1 overflow-y-auto bg-canvas p-4 sm:p-6 pb-12 md:pb-6">
          {/* Platform announcements — inside <main>, so the strip scrolls with
              the page rather than eating fixed height on a phone, and above
              {children} so it is the first thing read.

              HERE AND NOT IN App.js: the app-level siblings (the toaster, the
              version notifier) render outside all route gating, which includes
              the public quotation, invoice and payment-link pages customers
              open from an SMS. A platform maintenance notice on a customer's
              payment link is a message to the wrong audience. Layout is
              authenticated-only. */}
          <AnnouncementBar />
          {children}
        </main>

        {/* Phone bottom navigation. Staff get their full menu + Logout; everyone
            else gets curated primary tabs + a "More" button that opens the drawer. */}
        {isStaffExperience ? (
          <MobileBottomNav
            menuItems={menuItems}
            onScan={() => setShowScanner(true)}
            onLogout={handleLogout}
          />
        ) : (
          <MobileBottomNav
            menuItems={mobilePrimary}
            onScan={() => setShowScanner(true)}
            onCreate={() => setQuickCreateOpen(true)}
            onMore={() => setSidebarOpen(true)}
          />
        )}
      </div>


      {/* Phone quick-create bottom sheet (opened by the center + FAB) */}
      <QuickCreateMenu open={quickCreateOpen} onClose={() => setQuickCreateOpen(false)} />

      {/* Mobile/tablet drawer overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 xl:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <CommandPalette
        isOpen={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        menuItems={menuItems}
        isStaff={roleName === 'Staff' || viewMode === 'staff'}
      />

      {showScanner && <QRScanner onScan={handleScan} onClose={() => setShowScanner(false)} />}

      {previewJobCard && (
        <JobCardPreviewModal jobCard={previewJobCard} onClose={() => setPreviewJobCard(null)} />
      )}
    </div>
  );
};

export default Layout;
